import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import DateRangePicker from '../common/DateRangePicker';
import heroVideo from '../../assets/video.mp4';
import { useLanguage } from '../../context/LanguageContext';
import apiClient from '../../utils/apiClient';

const HeroSearch = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [cities, setCities] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  // Refs
  const calendarRef = useRef(null);
  const locationRef = useRef(null);
  const videoRef = useRef(null);

  // Fermer le calendrier au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await apiClient.get('/vehicle-reference/locations/public');
        const rawData = response.data;
        console.log("🔍 Réponse API brute :", rawData);

        // 1. Trouver le tableau (parfois c'est direct, parfois dans data, parfois dans data.data)
        let itemsArray = [];
        if (Array.isArray(rawData)) {
          itemsArray = rawData;
        } else if (rawData?.data && Array.isArray(rawData.data)) {
          itemsArray = rawData.data;
        } else if (rawData?.data?.data && Array.isArray(rawData.data.data)) {
          itemsArray = rawData.data.data;
        }
        console.log("📦 Tableau d'items détecté :", itemsArray);

        // 2. Extraire le nom de la ville (tester TOUTES les clés possibles)
        const extractedCities = itemsArray.map(item => {
          if (typeof item === 'string') return item; // Si l'API renvoie déjà un tableau de strings
          return item?.cityName || item?.city_name || item?.name || item?.city || item?.nom || '';
        })
        .filter(name => typeof name === 'string' && name.trim() !== ''); // Nettoyer

        // 3. Supprimer les doublons éventuels
        const uniqueCities = [...new Set(extractedCities)];
        
        console.log("✅ Villes extraites avec succès :", uniqueCities);
        setCities(uniqueCities);
      } catch (error) {
        console.error("❌ Erreur lors de la récupération des villes :", error);
      }
    };

    fetchCities();
  }, []);

  // Formater la date pour l'affichage
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '-- --- --';
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return '-- --- --';
    const paddedDay = day.toString().padStart(2, '0');
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${paddedDay} ${months[month - 1]}`;
  };

  const formatApiDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const handleSearch = () => {
    if (!location.trim() || !startDate || !endDate) {
      window.alert('Veuillez renseigner le lieu, la date de départ et la date de retour.');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start < today) {
      window.alert('La date de départ ne peut pas être dans le passé.');
      return;
    }

    if (end < start) {
      window.alert('La date de retour ne peut pas être antérieure à la date de départ.');
      return;
    }

    const formattedStart = formatApiDate(startDate);
    const formattedEnd = formatApiDate(endDate);
    const query = `/vehicles?city=${encodeURIComponent(location.trim())}&start=${formattedStart}&end=${formattedEnd}`;
    navigate(query);
  };

  const filteredCities = cities.filter(city => 
    city.toLowerCase().includes((location || '').toLowerCase().trim())
  );

  return (
    <div className="relative">
      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center bg-slate-900">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onEnded={() => setVideoEnded(true)}
            className={`w-full h-full object-cover ${videoEnded ? 'opacity-40' : 'opacity-50'}`}
            poster="https://images.unsplash.com/photo-1493238792015-fa094a367259?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent"></div>
        </div>

        <div className="container relative z-10 px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 drop-shadow-lg">
            <span className="hero-word">{t('hero.title')}</span>{' '}
            <span className="hero-word text-[#F5A623]">{t('hero.titleHighlight')}</span>
            <span className="hero-word">.</span>
          </h1>
          <p className="hero-subtitle text-gray-300 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>

          {/* Search Widget */}
          <div ref={calendarRef}>
            <div className="bg-white p-3 md:p-4 rounded-2xl shadow-2xl max-w-4xl mx-auto flex flex-col md:flex-row gap-3 items-center">
              {/* Lieu */}
              <div
                ref={locationRef}
                className="relative flex-1 w-full bg-gray-50 rounded-xl px-4 py-3 flex items-center hover:bg-gray-100 transition cursor-text group border border-transparent hover:border-[#27489E] focus-within:border-[#27489E] focus-within:bg-white"
              >
                <MapPin className="text-[#27489E] mr-3 flex-shrink-0" />
                <div className="text-left flex-1">
                  <label className="block text-[10px] font-bold text-gray-500 uppercase">{t('hero.location')}</label>
                  <input 
                    type="text" 
                    placeholder={t('hero.locationPlaceholder')}
                    value={location}
                    onChange={(e) => {
                      setLocation(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="bg-transparent outline-none w-full text-gray-800 font-medium placeholder-gray-400" 
                  />
                </div>
                {showSuggestions && location.length > 0 && (
                  <div className="absolute top-full left-0 mt-2 w-full md:w-[120%] bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-50">
                    {filteredCities.length > 0 ? (
                      <ul className="max-h-64 overflow-y-auto py-2 custom-scrollbar">
                        {filteredCities.map((city, index) => (
                          <li
                            key={index}
                            onClick={() => {
                              // Capitalise la première lettre lors de la sélection
                              const formattedCity = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                              setLocation(formattedCity);
                              setShowSuggestions(false);
                            }}
                            className="group flex items-center px-5 py-3 cursor-pointer transition-all duration-200 hover:bg-[#27489E]/10 border-l-4 border-transparent hover:border-[#27489E]"
                          >
                            <div className="p-2 mr-3 rounded-full bg-gray-50 group-hover:bg-white transition-colors duration-200">
                              <MapPin className="w-5 h-5 text-gray-400 group-hover:text-[#27489E]" />
                            </div>
                            <span className="font-medium text-gray-700 group-hover:text-[#27489E] capitalize text-base">
                              {city}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center px-4 py-8 text-gray-500">
                        <MapPin className="w-8 h-8 mb-2 text-gray-300" />
                        <p className="text-sm">Aucune ville trouvée pour "<span className="font-semibold">{location}</span>"</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Dates - Cliquable pour ouvrir le calendrier */}
              <div className="flex-1 w-full flex gap-2">
                {/* Date Départ */}
                <div 
                  onClick={() => setIsCalendarOpen(true)}
                  className={`flex-1 bg-gray-50 rounded-xl px-4 py-3 text-left hover:bg-gray-100 transition border cursor-pointer ${
                    isCalendarOpen ? 'border-[#27489E] bg-[#27489E]/5' : 'border-transparent hover:border-[#27489E]'
                  }`}
                >
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 cursor-pointer">{t('hero.departure')}</label>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-[#27489E] flex-shrink-0" />
                    <span className={`text-sm font-semibold ${startDate ? 'text-gray-800' : 'text-gray-400'}`}>
                      {formatDisplayDate(startDate)}
                    </span>
                  </div>
                </div>

                {/* Date Retour */}
                <div 
                  onClick={() => setIsCalendarOpen(true)}
                  className={`flex-1 bg-gray-50 rounded-xl px-4 py-3 text-left hover:bg-gray-100 transition border cursor-pointer ${
                    isCalendarOpen ? 'border-[#27489E] bg-[#27489E]/5' : 'border-transparent hover:border-[#27489E]'
                  }`}
                >
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 cursor-pointer">{t('hero.return')}</label>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-[#27489E] flex-shrink-0" />
                    <span className={`text-sm font-semibold ${endDate ? 'text-gray-800' : 'text-gray-400'}`}>
                      {formatDisplayDate(endDate)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSearch}
                className="w-full md:w-auto h-[64px] px-8 rounded-xl text-lg shadow-[#27489E]/30"
              >
                {t('hero.search')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Picker - Modal centré */}
      {isCalendarOpen && (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto p-3 md:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsCalendarOpen(false)}
          ></div>
          
          {/* Calendar */}
          <div ref={calendarRef} className="relative z-10 my-auto w-full max-w-[700px] animate-in zoom-in-95 duration-200">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onClose={() => setIsCalendarOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSearch;
